import { useToast } from 'components/useToast';
import { Invoice, trpc } from 'src/trpc';
import { Skeleton } from '@/components/ui/skeleton';
import { formatLongDate } from '@libs/services/date';
import { InvoiceSelectDetailsSheet } from './InvoiceSelectDetailsSheet';

export const InvoiceDetailsSection = ({
  invoice,
  refetchInvoice,
  isLoading,
}: {
  invoice: Invoice;
  refetchInvoice: () => void;
  isLoading?: boolean;
}) => {
  const { successToast, errorToast } = useToast();

  const { mutate: updateInvoiceDetails, isPending: isUpdating } =
    trpc.admin.invoice.adminUpdateInvoiceDetails.useMutation({
      onSuccess: async () => {
        successToast({
          title: 'Invoice Updated',
          description: 'Invoice has been updated and email sent to customer.',
        });
        refetchInvoice();
      },
      onError: () => {
        errorToast({
          title: 'Update Failed',
          description: 'Failed to update invoice. Please try again.',
        });
      },
    });

  if (!invoice) return null;

  const handleSave = async (customerNotes: string, dueDate: string) => {
    updateInvoiceDetails({
      customerNotes,
      dueDate,
      invoiceId: invoice._id,
    });
  };

  return (
    <>
      <div className="card relative min-w-[400px] w-full flex flex-col gap-4 items-stretch">
        <div className="space-y-4">
          <div>
            <InvoiceSelectDetailsSheet
              customerNotes={invoice.customerNotes || ''}
              dueDate={invoice.dueDate || ''}
              onSave={handleSave}
              isLoading={isUpdating || isLoading}
            />
          </div>

          <div className="space-y-4">
            <div>
              <label className="label text-xs">Issued Date</label>
              {isUpdating || isLoading ? (
                <Skeleton className="w-20 h-4" />
              ) : (
                <p className="text-text-primary text-sm font-medium">
                  {formatLongDate(invoice.issuedAt)}
                </p>
              )}
            </div>

            <div>
              <label className="label text-xs">Due Date</label>
              {isUpdating || isLoading ? (
                <Skeleton className="w-20 h-4" />
              ) : (
                <p className="text-text-primary text-sm font-medium">
                  {formatLongDate(invoice.dueDate)}
                </p>
              )}
            </div>
            {invoice.paidAt && (
              <div>
                <label className="label text-xs">Paid Date:</label>
                {isUpdating || isLoading ? (
                  <Skeleton className="w-20 h-4" />
                ) : (
                  <p className="text-text-primary text-sm font-medium">
                    {formatLongDate(invoice.paidAt)}
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="label text-xs">Customer Notes</label>
              {isUpdating || isLoading ? (
                <Skeleton className="w-20 h-4" />
              ) : (
                <p className="text-text-primary text-sm font-medium">
                  {invoice.customerNotes || 'No specific notes'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
